import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useOnboardingStore, UploadedMethodType } from '../../store/onboardingStore';
import { StepProductData } from './steps/StepProductData';
import { StepSalesAngles } from './steps/StepSalesAngles';
import { StepAdditionalOffers } from './steps/StepAdditionalOffers';
import { StepBrandAndPrice } from './steps/StepBrandAndPrice';
import { dictionary } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { axiosInstanceConversationalEngine } from '../../services/instance';
import { FunnelPayload, } from '../../services/interface';
import { postFunnel } from '../../services/funnels';
import { Clock, Users, Shield, Zap, Leaf } from 'lucide-react';
import Loader from '../common/Loader';
import { useTutorialContext } from '../../context/TutorialContext';
import { useNavigate } from 'react-router-dom';
// import { useTutorialContext } from '../../context/TutorialContext';
// import { TutorialSetup } from '../TutorialSetup';
// import WelcomeModal from '../WelcomeModal';
// import TutorialTooltip from '../TutorialTooltip';


const customToast = (message: string, title: string = 'Validation Error') => {
  return toast.custom(
    (t) => (
      <div
        className="max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5"
        style={{
          opacity: t.visible ? 1 : 0,
          transform: t.visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'all 0.3s ease-out',
        }}
      >
        <div className="p-4 w-full flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-lg font-medium text-gray-900">
              {title}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    {
      duration: 4000,
      position: 'top-right',
    }
  );
};

export function OnboardingLayout() {
  const { data, nextStep, previousStep, updateProductData, updateVisualContent, setSalesAngles, updateAdditionalOffers, updateContextData, contextData, hasFetchedSalesAngles, setHasFetchedSalesAngles, } = useOnboardingStore();
  const { visualContent, productData } = data;
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useAuth();
  const { step } = data;
  const [productCompleteness, setProductCompleteness] = useState<any>(data.additionalOffers);
  const { resumeTutorial, setCurrentStepIndex, isPaused } = useTutorialContext();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (step === 1 && isPaused) {
      setCurrentStepIndex(1);
      resumeTutorial();
    }
  }, [step]);

  useEffect(() => {
    setProductCompleteness(data.additionalOffers);
  }, [data.additionalOffers]);

  // const {
  //   isWelcomeOpen,
  //   isTutorialActive,
  //   currentStep: tutorialStep,
  //   startTutorial,
  //   nextStep: nextTutorialStep,
  //   closeTutorial
  // } = useTutorialContext();

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <StepProductData />;
      case 2:
        return <StepAdditionalOffers />;
      case 3:
        return <StepBrandAndPrice />;
      case 4:
        return <StepSalesAngles />;
      default:
        return <StepProductData />;
    }
  };

  const handleProductScrape = async () => {
    if (!data.productData.productLink) {
      return true;
    }

    setIsLoading(true);
    let success = false;

    try {
      const response = await axiosInstanceConversationalEngine.post('/scrape-product', {
        product_url: data.productData.productLink
      });

      const productData = response.data.data;

      if (productData.name == "unknown" || productData.description == "unknown" || productData.images.length == 0) {
        customToast(dictionary["product-not-found"][language], dictionary["product-not-found-title"][language]);
        return false;
      }

      let variants = null;
      if (productData.provider_id === 'dropi') {
        variants = productData.variants || null;
      }

      updateProductData({
        ...data.productData,
        name: productData.name,
        price: productData?.external_sell_price?.toString() || '0',
        details: productData.description,
        isManualEntry: false,
        externalId: productData.external_id,
        providerId: productData.provider_id,
        variants: variants
      });

      fetchSalesAngles({ productName: productData.name, productDescription: productData.description });

      if (productData.images && productData.images.length > 0) {
        updateVisualContent({
          images: productData.images
        });
      }

      if (!productData.name || !productData.description) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
      } else {
        success = true;
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      customToast('Failed to fetch product data. Please try again.');
      success = false;
    } finally {
      setIsLoading(false);
    }

    return success;
  };

  const isLastStep = step === 4;

  const handleProductCompleteness = (data: any) => {
    updateAdditionalOffers(data);
    setProductCompleteness(data);
  }

  interface FetchSalesAnglesProps {
    productName?: string;
    productDescription?: string;
  }

  const fetchSalesAngles = async ({ productName = '', productDescription = '' }: FetchSalesAnglesProps = {}) => {
    if (hasFetchedSalesAngles) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstanceConversationalEngine.post('/resolve-info-funnel', {
        product_name: productData?.name || productName,
        product_description: productData?.details || productDescription,
        language: language,
      });
      // const response = {
      //   data: {
      //     sales_angles: [
      //       { id: '1', name: 'Angle 1', description: 'Description 1', },
      //       { id: '2', name: 'Angle 2', description: 'Description 2' },
      //       { id: '3', name: 'Angle 3', description: 'Description 3' },
      //     ],
      //     pain_detection: 'Pain detection',
      //     buyer_detection: 'Buyer detection',
      //   }
      // };

      if (response.data && Array.isArray(response.data.sales_angles) && response.data.sales_angles.length > 0) {
        const apiSalesAngles = response.data.sales_angles.map((angle: any, index: number) => {
          const iconMap = [Clock, Users, Shield, Zap, Leaf];
          const id = angle.name ? angle.name.toLowerCase().replace(/\s+/g, '-') : `angle-${index}`;

          return {
            id,
            title: angle.name || `Angle ${index + 1}`,
            description: angle.description || 'No description available',
            icon: iconMap[index % iconMap.length],
            isRecommended: index === 0
          };
        });

        updateContextData({
          pain_detection: response.data.pain_detection || '',
          buyer_detection: response.data.buyer_detection || '',
          recommended_sales_angles: response.data.sales_angles || [],
        });

        setSalesAngles(apiSalesAngles);

        if (!visualContent.selectedAngle) {
          updateVisualContent({
            selectedAngle: apiSalesAngles[0].id
          });
        }
      }
    } catch (error) {
      console.error("Error fetching sales angles:", error);
      updateContextData({
        pain_detection: 'Error fetching data.',
        buyer_detection: 'Error fetching data.',
        recommended_sales_angles: [],
      });
    } finally {
      setIsLoading(false);
      setHasFetchedSalesAngles(true);
    }
  };

  const handleContinue = async () => {
    if (step === 1) {
      if (data.productData.productLink &&
        (!data.productData.name || !data.productData.details || !data.visualContent.images?.length)) {
        const scrapingSuccessful = await handleProductScrape();

        if (!scrapingSuccessful) return;
        nextStep();
        return;
      }

      if (!data.productData.name || !data.productData.details || (!data.visualContent.images?.length && !data.visualContent.imagesBase64?.length)) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
        return;
      }

      fetchSalesAngles();
      nextStep();
    } else if (step === 2) {
      const shouldScrapeBumpOffer = data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productLink && data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.LINK;

      const upsellEnabled = data.additionalOffers.upsell?.enabled &&
        data.additionalOffers.upsell.products?.some(p =>
          p.productLink || (p.productName && p.price && p.description && p.image)
        );


      setIsLoading(true);

      try {
        const promises = [];

        if (shouldScrapeBumpOffer &&
          (!data.additionalOffers.bumpOffer?.isManualEntry ||
            (!data.additionalOffers.bumpOffer.productName &&
              !data.additionalOffers.bumpOffer.price &&
              !data.additionalOffers.bumpOffer.description))) {

          const bumpOfferPromise = axiosInstanceConversationalEngine.post('/scrape-product', {
            product_url: data.additionalOffers.bumpOffer?.productLink || ''
          }).then(response => {
            return { type: 'bumpOffer', data: response.data.data };
          });

          promises.push(bumpOfferPromise);
        }

        if (upsellEnabled) {
          const upsellPromises = data.additionalOffers.upsell.products
            .filter(product => product.productLink && !product.isManualEntry)
            .map((product, index) => {
              return axiosInstanceConversationalEngine.post('/scrape-product', {
                product_url: product.productLink
              }).then(response => {
                return {
                  type: 'upsell',
                  index: index,
                  originalProduct: product,
                  data: response.data.data
                };
              });
            });

          promises.push(...upsellPromises);
        }

        const results = await Promise.all(promises);

        const updatedAdditionalOffersNew = { ...data.additionalOffers };
        const updatedUpsellProducts = [...data.additionalOffers.upsell.products];

        results.forEach(result => {
          if (result.type === 'bumpOffer' && data.additionalOffers.bumpOffer) {
            const productData = result.data;
            updatedAdditionalOffersNew.bumpOffer = {
              ...data.additionalOffers.bumpOffer,
              productName: productData.name || '',
              price: productData.external_sell_price ? productData.external_sell_price.toString() : '0',
              description: productData.description || '',
              url_images: productData.images && productData.images.length > 0 ? productData.images[0] : '',
              image: '',
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          } else if (result.type === 'upsell') {
            const productData = result.data;
            updatedUpsellProducts[(result as any).index] = {
              ...(result as any).originalProduct,
              productName: productData.name,
              price: productData.external_sell_price.toString(),
              description: productData.description,
              image: '',
              url_images: productData.images?.[0] ? [productData.images[0]] : [],
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          }
        });

        if (upsellEnabled) {
          updatedAdditionalOffersNew.upsell = {
            ...data.additionalOffers.upsell,
            products: updatedUpsellProducts
          };
        }

        handleProductCompleteness(updatedAdditionalOffersNew);

        if (updatedAdditionalOffersNew.bumpOffer?.enabled) {
          const bumpOffer = updatedAdditionalOffersNew.bumpOffer;
          if (!bumpOffer.productName || !bumpOffer.description || (!bumpOffer.image && !bumpOffer.url_images)) {
            customToast(dictionary["order-bump-information-incomplete"][language]);
            setIsLoading(false);
            return;
          }
        }

        if (updatedAdditionalOffersNew.upsell?.enabled && updatedAdditionalOffersNew.upsell.products?.length > 0) {
          for (const product of updatedAdditionalOffersNew.upsell?.products) {
            if (!product.productName || !product.price || !product.description || (!product.image && !((product.url_images ?? []).length > 0))) {
              customToast(dictionary["upsell-information-incomplete"][language]);
              setIsLoading(false);
              return;
            }
          }
        }

        nextStep();
      } catch (err) {
        console.error('Error fetching product data:', err);
        customToast('Failed to fetch product data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else if (step === 3) {
      if (data.productData.price === '0' || !data.productData.price || !data.productData.price.trim() || parseFloat(data.productData.price) < 0) {
        customToast(dictionary["price-required"][language], dictionary["main-product-price-incomplete-title"][language]);
        return;
      }
      if(data.additionalOffers.bumpOffer?.enabled){
        if (data.additionalOffers.bumpOffer?.price === '0' || !data.additionalOffers.bumpOffer?.price || !data.additionalOffers.bumpOffer?.price.trim() || parseFloat(data.additionalOffers.bumpOffer?.price) < 0) {
          customToast(dictionary["price-required"][language], dictionary["order-bump-product-price-incomplete-title"][language]);
          return;
        }
      }
      if(data.additionalOffers.upsell?.enabled){
        if (data.additionalOffers.upsell?.products?.some(product => product.price === '0' || !product.price || !product.price.trim() || parseFloat(product.price) < 0)) {
          customToast(dictionary["price-required"][language], dictionary["upsell-product-price-incomplete-title"][language]);
          return;
        }
      }

      nextStep();
    } else if (step === 4) {
      if (!data.visualContent.customAngle && !data.visualContent.selectedAngle || data.visualContent.selectedAngle.length < 1) {
        customToast(dictionary["please-select-a-minimum-of-1-marketing-angle"][language], dictionary["marketing-angle-required"][language]);
        return;
      }

      nextStep();
    } else {
      nextStep();
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (parseFloat(data.productData.price) === 0) {
        customToast('The main product price cannot be 0.', 'Main Product Price Incomplete');
        setIsLoading(false);
        return;
      }

      const products: any = [
        {
          external_id: data.productData.externalId || null,
          provider_id: data.productData.providerId || null,
          name: data.productData.name,
          description: data.productData.details,
          price: parseFloat(data.productData.price),
          url_images: data.visualContent.images || [],
          images: data.visualContent.imagesBase64 || [],
          type: "PRINCIPAL",
          variants: data.productData.variants || null
        }
      ];

      let upsellCount = 1;
      if (productCompleteness && productCompleteness.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
        productCompleteness.upsell.products.forEach((product: any) => {
          if (parseFloat(product.price) === 0) {
            customToast('The upsell product price cannot be 0.', 'Upsell Product Price Incomplete');
            setIsLoading(false);
            return;
          }

          if (product.productName && product.price) {
            products.push({
              external_id: product.externalId || null,
              provider_id: product.providerId || null,
              name: product.productName,
              description: product.description || "",
              price: parseFloat(product.price) || 0,
              url_images: product.url_images || [],
              // images: product.image ? [product.image] : [],
              type: "UP_SELL"
            });
            upsellCount++;
          }
        });
      }

      if (productCompleteness && productCompleteness.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
        if (parseFloat(productCompleteness.bumpOffer.price) === 0) {
          customToast('The order bump price cannot be 0.', 'order bump Price Incomplete');
          setIsLoading(false);
          return;
        }

        products.push({
          external_id: productCompleteness.bumpOffer.externalId || null,
          provider_id: productCompleteness.bumpOffer.providerId || null,
          name: productCompleteness.bumpOffer.productName,
          description: productCompleteness.bumpOffer.description || "",
          price: parseFloat(productCompleteness.bumpOffer.price) || 0,
          url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
          // images: productCompleteness.bumpOffer.image ? [productCompleteness.bumpOffer.image] : [],
          type: "BUMP_OFFER"
        });
      }

      const warrantyDays = data.productData.warranty === 'none' ? 0 :
        data.productData.warranty === 'custom' ?
          parseInt(data.productData.customWarrantyDays) : 30;

      const payload: FunnelPayload = {
        products: products,
        brand_name: data.visualStyle.brandName || "",
        sale_angles: data.visualContent.selectedAngle,
        free_shipping: data.productData.freeShipping,
        warranties: warrantyDays,
        template_design: {
          theme: data.visualContent.selectedAngle[0].name || "default",
          primary_color: data.visualStyle.colorPalette || "#FFFFFF",
          secondary_color: "#FFFFFF"
        },
        data_for_context: {
          pain_detection: contextData.pain_detection,
          buyer_detection: contextData.buyer_detection,
          recommended_sales_angles: contextData.recommended_sales_angles
        }
      };

      await postFunnel(payload);
      navigate("/funnels");
      // window.location.href = window.location.origin + "/funnels"

    } catch (error) {
      console.error('Error submitting form:', error);
      customToast('There was an error creating your funnels. Please try again.', 'Funnels Creation Failed');
      setIsLoading(false);
    }
  };

  const stepIsDisabled = step === 3 && data.visualStyle.brandName === '';
  const isButtonDisabled = stepIsDisabled || isLoading;

  return (
    <>
      {/* <TutorialSetup />
      <WelcomeModal
        isOpen={isWelcomeOpen}
        onStart={startTutorial}
        onClose={closeTutorial}
      />
      <TutorialTooltip
        step={tutorialStep}
        onNext={nextTutorialStep}
        onClose={closeTutorial}
        isActive={isTutorialActive}
      /> */}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-[50px] shadow-xl p-8 md:p-12">
            {renderStepContent()}

          <div className={`${step === 1 ? 'flex justify-end items-end' : 'flex justify-between items-center'} mt-8`}>
            {step > 1 && (
              <button
                onClick={previousStep}
                className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                {dictionary["back"][language]}
              </button>
            )}

            <div className="flex items-center justify-end gap-4">
              {step === 4 && <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F1F5F9] rounded-xl text-sm text-[#64748B]">
                <span className="font-extrabold text-gray-700">{dictionary["cost"][language]}:</span>
                <span className="font-extrabold text-gray-700">{visualContent.selectedAngle.length} {dictionary["credits"][language]}</span>
              </div>}

              <button
                disabled={isButtonDisabled}
                onClick={isLastStep ? handleSubmit : handleContinue}
                className={`${isButtonDisabled ? 'bg-[#E2E8F0]' : 'bg-[#3E3EFF] hover:bg-[#2d3a7f]'} ml-auto flex items-center gap-2 px-8 py-3 text-white rounded-full font-medium transition-colors`}
              >
                {isLoading ? <Loader size={20} color="#FFFFFF" /> : null}
                {isLastStep ? dictionary["create-page"][language] : dictionary["continue"][language]}
                {!isLastStep && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
